<script lang="ts" setup>
import { onMounted, onUnmounted, useRoute } from '#imports';
import { useService } from '@kaokei/use-vue-service';
import { getWechatConfigInfo } from '@core/api/login';
import { LoginService } from '@core/services/login.service';
import { UserService } from '@core/services/user.service';
import { ZoneService } from '@core/services/zone.service';
import { WX_SDK_URL } from '@core/utils/constant';
import {
  recreateWechatLoginOff,
  recreateWechatLoginOn,
} from '@core/utils/event-bus';
import { loadScript } from '@core/utils/loadScript';

const route = useRoute();
const userService = useService(UserService);
const service = useService(LoginService);
const zoneService = useService(ZoneService);

const SITE_URL = zoneService.getSiteUrl();
const o = encodeURIComponent(window.location.origin);
const r = encodeURIComponent(window.location.href);
const redirectUri = `${SITE_URL}/callback/wechat/?o=${o}&redirect=${r}`;

const createWechatLogin = () => {
  getWechatConfigInfo().then((res) => {
    loadScript(WX_SDK_URL).then(() => {
      new WxLogin({
        self_redirect: true,
        id: 'wechat-login-qrcode-container',
        appid: res.appId,
        scope: 'snsapi_login',
        redirect_uri: encodeURIComponent(redirectUri),
        state: Date.now(),
        style: 'black',
        href: 'data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZXt3aWR0aDoxOTBweDtib3JkZXI6MXB4IHNvbGlkIHJnYmEoMCwgMCwgMCwgMC4xNSk7Ym9yZGVyLXJhZGl1czo0cHh9Ci5pbXBvd2VyQm94IC50aXRsZXtkaXNwbGF5Om5vbmV9Ci5pbXBvd2VyQm94IC5pbmZve3dpZHRoOjI0MHB4fQouaW1wb3dlckJveCAuc3RhdHVze3RleHQtYWxpZ246Y2VudGVyfQouaW1wb3dlckJveCAuc3RhdHVzIHB7Y29sb3I6cmdiYSgwLCAwLCAwLCAwLjQ1KX0KLmltcG93ZXJCb3ggLnN0YXR1c19pY29ue2Rpc3BsYXk6bm9uZX0=',
      });
    });
  });
};

// 获取小程序码
const fetchQrcode = () => {
  service.getWechatLoginQrcode();
};

onMounted(() => {
  // createWechatLogin();
  // recreateWechatLoginOn(createWechatLogin);
  fetchQrcode(); // 初始加载小程序码
  recreateWechatLoginOn(fetchQrcode); // 监听重新创建事件
});

onUnmounted(() => {
  recreateWechatLoginOff();
});
</script>

<template>
  <div>
    <!-- <div class="mb-4 text-xl font-bold text-colorText">扫描二维码登录</div>
    <div id="wechat-login-qrcode-container"></div> -->

    <div class="text-colorText mb-4 text-xl font-bold">扫码登录</div>

    <div class="flex flex-col items-center">
      <div class="relative flex h-60 w-60 items-center justify-center">
        <img
          v-if="service.wechatLoginQrcode"
          :src="'data:image/png;base64,' + service.wechatLoginQrcode"
          alt="wechat-login"
          :width="160"
          :height="160"
          class="relative z-0 rounded"
        />

        <div
          v-if="
            service.wechatLoginLoading ||
            service.wechatLoginStatus === 'scanned' ||
            service.wechatLoginStatus !== 'success'
          "
          class="absolute inset-0 z-10 rounded bg-white opacity-90"
        ></div>

        <div
          class="absolute inset-0 z-20 flex flex-col items-center justify-center"
        >
          <!-- 加载状态 -->
          <div
            v-if="service.wechatLoginLoading"
            class="flex items-center justify-center"
          >
            <a-spin size="large" />
          </div>

          <!-- 错误状态 -->
          <div
            v-else-if="service.wechatLoginStatus === 'failed'"
            class="flex flex-col items-center justify-center"
          >
            <p class="mt-2 text-center text-red-500">
              {{ service.wechatLoginError || '获取小程序码失败' }}
            </p>
            <a-button
              type="primary"
              size="small"
              class="mt-2"
              @click="fetchQrcode"
            >
              重新获取
            </a-button>
          </div>

          <!-- 已扫码状态 -->
          <div
            v-else-if="service.wechatLoginStatus === 'scanned'"
            class="flex flex-col items-center justify-center"
          >
            <p class="text-[rgba(0, 0, 0, 0.88)] mt-1 text-sm">已扫描</p>
          </div>

          <!-- 登录成功状态 -->
          <div
            v-else-if="service.wechatLoginStatus === 'loginSuccess'"
            class="flex flex-col items-center justify-center"
          >
            <CheckCircleOutlined class="text-4xl text-green-500" />
            <p class="mt-2 text-center text-green-500">登录成功</p>
          </div>

          <!-- 失效状态 -->
          <div
            v-else-if="service.wechatLoginStatus === 'loginFailed'"
            class="flex flex-col items-center justify-center gap-2"
          >
            <p class="text-[rgba(0, 0, 0, 0.88)] mt-1 text-sm">
              小程序码已过期
            </p>
            <a-button type="primary" size="small" @click="fetchQrcode">
              获取小程序码
            </a-button>
          </div>
        </div>
      </div>

      <div
        v-if="
          service.wechatLoginStatus === 'success' && service.wechatLoginQrcode
        "
      >
        <p class="mt-4 text-center text-sm text-gray-500">
          请使用微信扫码<br />
          有效期5分钟
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(#wechat-login-qrcode-container iframe) {
  width: 300px !important;
  height: 260px !important;
  margin: 0 auto;
}

.flex-col.items-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.w-60.h-60 {
  width: 160px;
  height: 160px;
  border-radius: 8px;
}

:deep(.ant-qrcode) {
  margin: 0 auto;
}
</style>
